import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { clsx } from 'clsx'
import { useMemo, useState } from 'react'

import { ConsentTermsModal } from './ConsentTermsModal'
import {
  getPrivacyConsentContent,
  getMarketingConsentContent,
} from './consent.content'

interface ConsentSectionProps {
  privacyAgreed: boolean
  marketingAgreed: boolean
  onPrivacyChange: (next: boolean) => void
  onMarketingChange: (next: boolean) => void
}

type OpenTerms = 'privacy' | 'marketing' | null

export function ConsentSection({
  privacyAgreed,
  marketingAgreed,
  onPrivacyChange,
  onMarketingChange,
}: ConsentSectionProps) {
  const { t: mt } = useTranslation('marketing')

  const [openTerms, setOpenTerms] = useState<OpenTerms>(null)
  const allAgreed = useMemo(
    () => privacyAgreed && marketingAgreed,
    [privacyAgreed, marketingAgreed],
  )

  const toggleAll = () => {
    const next = !allAgreed
    onPrivacyChange(next)
    onMarketingChange(next)
  }

  return (
    <div className="rounded-qb-md bg-qb-bg-secondary p-4 flex flex-col gap-3">
      <button
        type="button"
        onClick={toggleAll}
        className="flex items-center gap-3 text-left"
      >
        <CheckIcon checked={allAgreed} />
        <span className="text-qb-body-2 font-semibold text-qb-text-high">
          {mt('consent.all')}
        </span>
      </button>

      <div className="border-t border-qb-divider" />

      <Row
        checked={privacyAgreed}
        onToggle={() => onPrivacyChange(!privacyAgreed)}
        label={mt('consent.privacy')}
        required
        onView={() => setOpenTerms('privacy')}
      />

      <Row
        checked={marketingAgreed}
        onToggle={() => onMarketingChange(!marketingAgreed)}
        label={mt('consent.marketing')}
        required={false}
        onView={() => setOpenTerms('marketing')}
      />

      <ConsentTermsModal
        open={openTerms === 'privacy'}
        onClose={() => setOpenTerms(null)}
        content={getPrivacyConsentContent(mt)}
      />
      <ConsentTermsModal
        open={openTerms === 'marketing'}
        onClose={() => setOpenTerms(null)}
        content={getMarketingConsentContent(mt)}
      />
    </div>
  )
}

interface RowProps {
  checked: boolean
  onToggle: () => void
  label: string
  required: boolean
  onView: () => void
}

function Row({ checked, onToggle, label, required, onView }: RowProps) {
  const { t: mt } = useTranslation('marketing')

  return (
    <div className="flex items-center justify-between">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-3 text-left"
      >
        <CheckIcon checked={checked} small />
        <span className="text-qb-body-3 text-qb-text-mid">
          {label}{' '}
          <span className={required ? 'text-qb-primary' : 'text-qb-text-low'}>
            ({required ? mt('consent.required') : mt('consent.optional')})
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onView}
        className="text-qb-body-4 text-qb-text-low hover:text-qb-text-mid"
      >
        {mt('consent.view')}
      </button>
    </div>
  )
}

function CheckIcon({ checked, small = false }: { checked: boolean; small?: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded-full transition-colors',
        small ? 'w-5 h-5' : 'w-6 h-6',
        checked ? 'bg-qb-primary text-white' : 'bg-qb-gray-200 text-white/0',
      )}
      aria-hidden
    >
      <Check className={clsx(small ? 'w-3.5 h-3.5' : 'w-4 h-4')} strokeWidth={3} />
    </span>
  )
}
